import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import Modal from '@components/Shared/Modal/Modal';
import Form from '@components/Shared/Form/Form';
import TextField from '@components/Shared/TextField';
import Button from '@components/Shared/Buttons/Button';
import { DXAccountContext } from '@contexts/DXAccountContext';
import { submitForm } from '@services/formService';
import { runCommonValidation } from 'pubweb-smokey/dist/utils/validation';
import EnvelopeClosedSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/envelope-closed.svg';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { pushGtmFormAction } from '@utils/gtmUtils';
import { setLocalStorage } from 'pubweb-smokey/dist/utils/utils';
import CheckmarkSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/checkmark.svg';
import { EmailSignupStdStyles } from './EmailSignupStd.styled';

const EmailSignupStd = ({
  onCloseEmailPopup,
  localStorageKey,
  pageType,
  modelNumber = null, // Model number is optional depending on page. It is only used for standard email pop-up on home details page form sumbmit event data.
}) => {
  const accountContext = useContext(DXAccountContext);
  const [formErrors, setFormErrors] = useState({});
  const [submittingForm, setSubmittingForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const originWebPageTypes = {
    homepage: 'clayton-homes-home-page',
    studio: 'clayton-content-landing-page',
    homedetail: 'clayton-homes-home-detail',
  };

  // We want form_submit_text parameter to match the submit button text. Added this const so the two would stay in sync if text was changed.
  const submitButtonText = 'Sign Me Up';
  const formSubmitText = submitButtonText
    .trim()
    .toLowerCase()
    .replaceAll(' ', '_');

  // The data sent with the form action event data is slightly different for each page the pop-up appears on.
  // The home details standard pop-up is the only one that uses model number at this time.
  const formActionEventData = {
    homepage: {
      form_action_type: 'submit',
      form_name: 'homepage',
      form_method: 'pop_up',
      form_id: 'email_pop_up_homepage',
      form_submit_text: formSubmitText,
    },
    studio: {
      form_action_type: 'submit',
      form_name: 'studio_homepage',
      form_method: 'pop_up',
      form_id: 'email_pop_up_studio_homepage',
      form_submit_text: formSubmitText,
    },
    homedetail: {
      form_action_type: 'submit',
      form_name: 'home_details',
      form_method: 'pop_up',
      form_id: 'email_pop_up_home_details',
      form_submit_text: formSubmitText,
      model_number: modelNumber,
    },
  };

  // Default to homepage in case an invalid type is received.
  const originWebPageType =
    originWebPageTypes[pageType] || originWebPageTypes['homepage'];
  const formActionEvent =
    formActionEventData[pageType] || formActionEventData['homepage'];

  const handleValidation = (formState) => {
    const validationErrors = runCommonValidation(formState);
    setFormErrors(validationErrors);
    return validationErrors.hasErrors;
  };

  const handleSubmit = (formData) => {
    if (submittingForm || formSubmitted) {
      return;
    }

    setSubmittingForm(true);
    const btn = document.getElementById('email-subscribe-button');
    if (btn) {
      btn.setAttribute('disabled', true);
    }

    const formSubmission = {
      ...formData,
      canEmail: true,
      originWebFormType: 'marketing-form',
      originWebApp: 'clayton-homes-website',
      originWebPageType: originWebPageType,
      originCampaign: '',
      originWebFormCategory: 'email-sign-up',
      claytonAccountId: accountContext.state.account?.accountId || null,
      dataExtension: 'FormActivityCHContent',
    };

    submitForm(formSubmission)
      .then(() => {
        pushGTMEvent('gtm-chcom-email-subscribe', 'email_subscribe', {
          category: 'home_consideration',
          action: 'form',
        }); // Per Digital Marketing, also keep this old UA event for now. It should be removed at a later time after GA4 event evaluation.
        pushGtmFormAction({ ...formActionEvent });

        if (typeof window !== 'undefined') {
          window.analytics.identify(accountContext.state?.account?.userId, {
            firstName:
              accountContext.state.account?.firstName || formData.firstName,
            lastName:
              accountContext.state.account?.lastName || formData.lastName,
            phone: accountContext.state.account?.phone || formData.phone,
            email: accountContext.state?.email || formData.email,
          });
        }

        setSubmittingForm(false);
        setFormSubmitted(true);
      })
      .catch(() => {
        setSubmittingForm(false);
        setFormSubmitted(false);

        setFormErrors({
          email: 'An error occurred, please try again.',
        });
      });
  };

  return (
    <EmailSignupStdStyles>
      <Modal
        onClose={() => {
          pushGTMEvent('gtm-chcom-close-email-popup', 'email_exit', {
            location: 'Homepage',
            category: 'brand_education',
            action: 'button',
            label: 'ch.closeEmailPopup',
          });
          setLocalStorage(localStorageKey, true);
          onCloseEmailPopup();
        }}
      >
        <div
          className="email-image"
          style={{
            backgroundImage: `url('/public-assets/images/email-signup/fall-email-pop-up-3.png')`,
          }}
        ></div>
        <div className="email-content">
          <div>
            <EnvelopeClosedSvg viewBox="0 0 16 16" />
          </div>
          <h1>Browse Beautiful Homes in Your Inbox!</h1>
          <h4>
            Get new product updates and homeowner tips when you sign up for
            email.
          </h4>
          <div className="webform-container">
            {formSubmitted ? (
              <div className="webform-thankyou">
                <div>
                  <CheckmarkSvg viewBox="0 0 16 16" />
                </div>
                <span className="webform-thankyou body-text">
                  Thank you for subscribing!
                </span>
              </div>
            ) : (
              <Form onSubmit={handleSubmit} onFormValidate={handleValidation}>
                <TextField
                  id="email"
                  label="Email Address"
                  defaultValue={accountContext.state?.email}
                  errorMessage={formErrors.email}
                  automationId="email-web-form-text-field"
                />
                <Button
                  className={`gtm-email-subscribe ${
                    submittingForm ? 'submitting' : ''
                  }`}
                  id="email-subscribe-button"
                  buttonStyle={submittingForm ? 'solid disabled' : 'solid'}
                  fillContainer={false}
                  automationId="email-web-form-button"
                >
                  <span className="webform-button">{submitButtonText}</span>
                </Button>
              </Form>
            )}
          </div>
          <p>
            By entering your email address, you agree to receive marketing
            emails from Clayton. Unsubscribe any time.
          </p>
        </div>
      </Modal>
    </EmailSignupStdStyles>
  );
};

EmailSignupStd.propTypes = {
  localStorageKey: PropTypes.any,
  onCloseEmailPopup: PropTypes.func,
  pageType: PropTypes.string,
  modelNumber: PropTypes.string,
};

export default EmailSignupStd;
